"use client";

import { useEffect, useRef, useState } from "react";
import { ShoppingBag } from "lucide-react";
import { useTranslations } from "next-intl";
import { CART_OPEN_EVENT, getBasket, subscribeBasket, updateBasketLine, type BasketLine } from "@/lib/basket";
import { getPreview } from "@/lib/api";
import { getMarketingCookies } from "@/lib/fbq";
import { useIdleModule } from "@/lib/idlePreload";

// The drawer, with framer-motion, the print ladder and the lightbox, is its own chunk: this button
// is in the header of every page, and in the first-load JS that drawer was the biggest thing on
// the landers for a panel that is closed on arrival. It is fetched once the page is idle after
// `load`, or as soon as a pointer or focus reaches the bag, so it is there before the first tap.
const loadDrawer = () => import("@/components/CartDrawer");

// The key the open drawer's history entry carries, next to the router's own state.
const CART_HISTORY_KEY = "vellumCart";

function preloadDrawer() {
  void loadDrawer();
}

export function CartButton() {
  const t = useTranslations("cart");
  const [lines, setLines] = useState<BasketLine[]>([]);
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  // Set on the first open and never cleared: the drawer stays mounted, so its close animation plays.
  const [drawerMounted, setDrawerMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setLines(getBasket());
    const unsubscribe = subscribeBasket(() => setLines(getBasket()));
    // Self-heal lines saved before we captured a thumbnail (older baskets / a stash that
    // didn't carry one): fetch the preview image once and patch it back into the line.
    let cancelled = false;
    void (async () => {
      for (const line of getBasket()) {
        if (line.thumbnailUrl) continue;
        try {
          const data = await getPreview(line.jobId, getMarketingCookies());
          if (!cancelled && data.watermarkedImageUrl) updateBasketLine(line.jobId, { thumbnailUrl: data.watermarkedImageUrl });
        } catch {
          // best-effort — leave the placeholder if the preview can't be fetched.
        }
      }
    })();
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // "Go to cart" elsewhere on the page (My designs) opens this drawer.
  useEffect(() => {
    const openDrawer = () => setOpen(true);
    window.addEventListener(CART_OPEN_EVENT, openDrawer);
    return () => window.removeEventListener(CART_OPEN_EVENT, openDrawer);
  }, []);

  useEffect(() => {
    if (open) setDrawerMounted(true);
  }, [open]);

  // Back closes the drawer instead of leaving the page: opening it pushes a history entry (same URL,
  // the router's own state plus a marker), and a popstate while it is open just closes it. Closing it
  // any other way (X, backdrop, Escape) steps back off that entry, but only while it is still ours on
  // top, so Back afterwards goes where it went before the drawer opened. A close that navigates
  // (Checkout, "+ add another") clears the marker first and the drawer navigates with replace, so
  // the destination takes the entry's place and Back from it returns to this page, not to a drawer.
  const historyMarker = useRef<string | null>(null);
  useEffect(() => {
    if (!open) return;
    const marker = String(Date.now());
    window.history.pushState({ ...window.history.state, [CART_HISTORY_KEY]: marker }, "");
    historyMarker.current = marker;
    const onPopState = () => {
      historyMarker.current = null;
      setOpen(false);
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      const pushed = historyMarker.current;
      historyMarker.current = null;
      if (pushed && window.history.state?.[CART_HISTORY_KEY] === pushed) window.history.back();
    };
  }, [open]);

  const closeForNavigation = () => {
    historyMarker.current = null;
    setOpen(false);
  };

  // The drawer's module: imported once the page is idle after load, or on the first open.
  const CartDrawer = useIdleModule(loadDrawer, open)?.CartDrawer;

  const count = lines.length;

  return (
    <>
      <button
        aria-label={t("aria", { count })}
        className="relative -mr-1 flex h-9 w-9 items-center justify-center text-ink-soft transition-colors hover:text-ink"
        data-testid="cart-button"
        onClick={() => setOpen(true)}
        onFocus={preloadDrawer}
        onPointerDown={preloadDrawer}
        onPointerEnter={preloadDrawer}
        type="button"
      >
        <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.5} />
        {mounted && count > 0 ? (
          <span
            aria-hidden
            className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 font-mono text-[10px] font-semibold leading-none text-paper"
            data-testid="cart-badge"
          >
            {count}
          </span>
        ) : null}
      </button>

      {(drawerMounted || open) && CartDrawer ? (
        <CartDrawer
          lines={lines}
          onClose={() => setOpen(false)}
          onCloseForNavigation={closeForNavigation}
          onLinesChange={setLines}
          open={open}
        />
      ) : null}
    </>
  );
}
